import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eyebrow, Pill, SectionStamp } from '../components/Atoms';
import Icon from '../components/Icon';
import TopBar from '../components/TopBar';
import TabBar from '../components/TabBar';
import WhatsAppFab from '../components/WhatsAppFab';
import { fonts } from '../theme';

export default function Home({ t }) {
  const navigate = useNavigate();

  return (
    <div style={{
      width: '100%', height: '100%', position: 'relative',
      overflow: 'hidden', background: t.paper, color: t.ink,
    }}>
      {/* HERO BLOCK — dark slab */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 540,
        background: t.ink, color: t.paper,
        clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 28px), 0 100%)',
      }}>
        <TopBar t={t} dark={true} title="Dr. Moksha Patel" />

        <div style={{ padding: '14px 22px 0' }}>
          <Eyebrow t={t} color={t.mint}>Physiotherapy · Vadodara</Eyebrow>
          <h1 style={{
            fontFamily: fonts.serif, fontWeight: 300,
            fontSize: 54, lineHeight: 0.95, letterSpacing: '-0.035em',
            margin: '14px 0 0', color: t.paper,
          }}>
            <span style={{ display: 'block' }}>Move</span>
            <span style={{ display: 'block', fontStyle: 'italic', color: t.mint, fontWeight: 400 }}>better,</span>
            <span style={{ display: 'block' }}>heal slower.</span>
          </h1>

          <p style={{
            fontFamily: fonts.sans, fontSize: 13.5, lineHeight: 1.5,
            color: 'rgba(246,243,236,0.72)',
            margin: '18px 0 0', maxWidth: 290,
          }}>
            Hands-on physiotherapy by <em style={{ fontStyle: 'italic', color: t.mint }}>Dr. Moksha Patel (PT)</em> — orthopaedic, neuro, paediatric &amp; post-op care, at the clinic or in your home.
          </p>
        </div>

        {/* Photo plate */}
        <div style={{
          position: 'absolute', right: 22, bottom: -40, width: 150, height: 200,
          borderRadius: 8, overflow: 'hidden', border: `1px solid ${t.mint}33`,
        }}>
          <img src={`${process.env.PUBLIC_URL}/assets/hero.webp`} alt="Dr. Moksha Patel" style={{
            width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top',
            background: t.tint,
            filter: 'saturate(0.85) contrast(1.02)',
          }} />
          <div style={{
            position: 'absolute', left: 8, bottom: 8,
            fontFamily: fonts.mono, fontSize: 8.5, color: t.paper,
            letterSpacing: '0.16em', textTransform: 'uppercase',
            background: 'rgba(0,0,0,0.4)', padding: '3px 6px', borderRadius: 3,
            backdropFilter: 'blur(8px)',
          }}>GPC-21250</div>
        </div>

        {/* CTAs */}
        <div style={{ position: 'absolute', left: 22, bottom: 60, display: 'flex', gap: 10, flexDirection: 'column' }}>
          <Pill t={t} fill={t.mint} ink={t.ink} icon="calendar" onClick={() => navigate('/book')}>
            Book a session
          </Pill>
          <button onClick={() => navigate('/about')} style={{
            appearance: 'none', border: 'none', background: 'transparent', cursor: 'pointer',
            color: t.paper, padding: '0 4px', textAlign: 'left',
            fontFamily: fonts.sans, fontSize: 12, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            opacity: 0.8,
          }}>
            <span>Read about Dr. Moksha</span>
            <Icon name="arrow-right" size={14}/>
          </button>
        </div>
      </div>

      {/* SCROLL CONTENT */}
      <div style={{
        position: 'absolute', top: 540, left: 0, right: 0, bottom: 0,
        overflowY: 'auto', padding: '34px 22px 100px',
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14,
          paddingBottom: 24, borderBottom: `0.5px solid ${t.hair}`, marginBottom: 24,
        }}>
          {[
            { k: '4+', v: 'years of clinical practice' },
            { k: '5', v: 'specialties' },
            { k: 'Home', v: 'visits in Vadodara' },
          ].map((s, i) => (
            <div key={i}>
              <div style={{
                fontFamily: fonts.serif, fontSize: 28, fontWeight: 400,
                lineHeight: 1, letterSpacing: '-0.03em', color: t.ink,
              }}>{s.k}</div>
              <div style={{
                fontFamily: fonts.sans, fontSize: 11, lineHeight: 1.35,
                color: t.muted, marginTop: 6,
              }}>{s.v}</div>
            </div>
          ))}
        </div>

        <SectionStamp n="01" label="Care" t={t} />
        <h2 style={{
          fontFamily: fonts.serif, fontSize: 28, fontWeight: 400,
          letterSpacing: '-0.03em', lineHeight: 1.05,
          margin: '12px 0 18px', color: t.ink,
          textWrap: 'balance',
        }}>
          Five practices, <span style={{ fontStyle: 'italic' }}>one</span> patient-first method.
        </h2>

        {[
          { ic: 'spine', title: 'Orthopaedic', desc: 'Joint pain, sports & post-injury.' },
          { ic: 'brain', title: 'Neurological', desc: 'Stroke, MS, Parkinson’s.' },
          { ic: 'baby', title: 'Paediatric', desc: 'Developmental & congenital care.' },
        ].map((row, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '14px 0',
            borderBottom: `0.5px solid ${t.hair}`,
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: 999,
              background: t.tint, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: t.mintDeep, flexShrink: 0,
            }}>
              <Icon name={row.ic} size={18} sw={1.6}/>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: fonts.sans, fontSize: 14, fontWeight: 600 }}>{row.title}</div>
              <div style={{ fontFamily: fonts.sans, fontSize: 11.5, color: t.muted, marginTop: 2 }}>{row.desc}</div>
            </div>
            <Icon name="arrow-right" size={16} stroke={t.muted}/>
          </div>
        ))}

        <button onClick={() => navigate('/services')} style={{
          appearance: 'none', border: `0.5px solid ${t.hair}`, background: 'transparent',
          width: '100%', padding: '14px', borderRadius: 12, marginTop: 14,
          fontFamily: fonts.sans, fontSize: 13, fontWeight: 500, color: t.ink, cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>All five specialties</span>
          <Icon name="arrow-up-right" size={16}/>
        </button>

        <div style={{
          marginTop: 36, borderRadius: 14, padding: 18,
          background: t.mint, color: t.ink, position: 'relative', overflow: 'hidden',
        }}>
          <Eyebrow t={t} color={t.mintDeep}>Home visits · Vadodara</Eyebrow>
          <div style={{
            fontFamily: fonts.serif, fontSize: 22, lineHeight: 1.05, fontWeight: 400,
            letterSpacing: '-0.02em', marginTop: 8, maxWidth: 230,
          }}>
            Care that comes to your <em style={{ fontStyle: 'italic' }}>doorstep.</em>
          </div>
          <button onClick={() => navigate('/contact')} style={{
            appearance: 'none', border: 'none', background: t.ink, color: t.paper, cursor: 'pointer',
            padding: '10px 14px', borderRadius: 999, marginTop: 14,
            fontFamily: fonts.sans, fontSize: 12, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            Request a visit <Icon name="arrow-up-right" size={14}/>
          </button>
          <Icon name="home" size={120} stroke={t.ink} sw={0.6}
            style={{ position: 'absolute', right: -20, top: -20, opacity: 0.08 }}/>
        </div>
      </div>

      <WhatsAppFab bottom={100}/>
      <TabBar t={t}/>
    </div>
  );
}
